/**
 * États transverses d'un écran : chargement, erreur, absence de données.
 *
 * Regroupés parce qu'ils apparaissent sur les dix écrans et que leur traitement doit être identique
 * partout — en particulier celui de l'erreur, qui doit montrer le message du serveur et non un texte
 * réécrit côté client (`docs/agent-spec/05-api-backend.md`).
 */
import { ErreurApi, MESSAGE_INDISPONIBLE } from "../api/erreurs";

export function Chargement({ libelle = "Chargement…" }: { libelle?: string }) {
  return (
    <p className="etat etat--chargement" role="status">
      {libelle}
    </p>
  );
}

export function Vide({ libelle }: { libelle: string }) {
  return <p className="etat etat--vide">{libelle}</p>;
}

export function Erreur({ erreur, reessayer }: { erreur: unknown; reessayer?: () => void }) {
  const message = erreur instanceof ErreurApi ? erreur.message : MESSAGE_INDISPONIBLE;

  return (
    <div className="etat etat--erreur" role="alert">
      <p>{message}</p>
      {erreur instanceof ErreurApi && erreur.details.length > 0 && (
        <ul>
          {erreur.details.map((detail, index) => (
            <li key={index}>
              {detail.field ? <strong>{detail.field} : </strong> : null}
              {detail.issue}
            </li>
          ))}
        </ul>
      )}
      {reessayer && (
        <button type="button" onClick={reessayer}>
          Réessayer
        </button>
      )}
    </div>
  );
}

/**
 * Valeur d'indicateur, ou la raison de son absence.
 *
 * `docs/DECISIONS.md` D32 : un indicateur non mesurable vaut `null` **avec sa raison**, jamais zéro.
 * Afficher un tiret perdrait cette information, et afficher `0` serait un mensonge — un parc dont on
 * ne sait pas mesurer la disponibilité n'a pas une disponibilité nulle.
 */
export function ValeurOuRaison({
  valeur,
  unite,
  indisponibleCar,
}: {
  valeur: number | null | undefined;
  unite?: string | null;
  indisponibleCar?: string | null;
}) {
  if (valeur === null || valeur === undefined) {
    return (
      <span className="valeur valeur--indisponible" title={indisponibleCar ?? undefined}>
        non mesurable
        {indisponibleCar ? <small> — {indisponibleCar}</small> : null}
      </span>
    );
  }
  return (
    <span className="valeur">
      {valeur.toLocaleString("fr-FR", { maximumFractionDigits: 2 })}
      {unite ? <small> {unite}</small> : null}
    </span>
  );
}

/**
 * Localisation d'un GRV, en tenant compte de la vue agrégée.
 *
 * Quand `localisation_agregee` vaut `true`, l'API a **omis** la latitude et la longitude et remplacé
 * le nom du site par celui de l'île (`docs/DECISIONS.md` D19, D46). Il faut le dire à l'utilisateur :
 * afficher seulement « Tahiti » laisserait croire à une position imprécise plutôt qu'à une
 * information volontairement agrégée.
 */
export function Localisation({
  zone,
  agregee,
}: {
  zone: string | null | undefined;
  agregee?: boolean | null;
}) {
  if (!zone) return <span className="etat--vide">position inconnue</span>;
  return (
    <span>
      {zone}
      {agregee ? <small className="localisation--agregee"> (localisation agrégée)</small> : null}
    </span>
  );
}
